import type { Messages } from './pt-BR.js';

export const en: Messages = {
  common: {
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    back: 'Back',
    add: 'Add',
    remove: 'Remove',
    edit: 'Edit',
    confirm: 'Confirm',
    loading: 'Loading',
    retry: 'Try again',
    refresh: 'Refresh',
    actions: 'Actions',
    details: 'Details',
    none: 'None',
    na: 'n/a',
    yes: 'Yes',
    no: 'No',
    all: 'All',
    optional: 'optional',
    readOnly: 'Read only',
    loadError: 'Could not load.',
    correlation: 'Support code: {{id}}',
    saved: 'Saved.',
    required: 'Required.',
    unsaved: 'You have unsaved changes. Leave anyway?',
    unsavedBadge: 'Unsaved changes',
  },
  errors: {
    network: 'No connection to the service. Check the network and try again.',
    forbidden: 'Your role does not allow this action.',
    conflict: 'Someone else saved first. The data was reloaded; make your change again.',
    session: 'Your session ended. Sign in again.',
    invalidResponse: 'The service answered in an unexpected format. Refresh the panel; if it keeps happening, open a ticket.',
    generic: 'Could not complete.',
  },
  nav: {
    label: 'Main menu',
    skip: 'Skip to content',
    overview: 'Overview',
    connections: 'Connections',
    clients: 'Apps',
    users: 'Users',
    collapse: 'Collapse menu',
    expand: 'Expand menu',
    groups: {
      context: 'Context',
      channels: 'Channels',
      audit: 'Audit',
      admin: 'Administration',
    },
    chat: 'Browser chat',
    teams: 'Ask in Teams',
    mcp: 'Connectors (Claude, GPT)',
    audit: 'Qlik queries',
    conversations: 'Conversations',
    appearance: 'Appearance',
    settings: 'Settings',
    ownerNotice: 'As a subject owner, you see your apps, with the quality of each one, and the Conversations about them.',
  },
  user: {
    menu: 'Account and preferences',
    theme: 'Theme',
    themeSystem: 'Follow the system',
    themeLight: 'Light',
    themeDark: 'Dark',
    language: 'Language',
    logout: 'Sign out',
    expires: 'Session until {{time}}',
    admin: 'Administrator',
    owner: 'Subject owner',
  },
  login: {
    title: 'Sign in to the panel',
    subtitle: 'Use your organization account. What you see depends on your role in the IdP.',
    sso: 'Sign in with the organization account',
    dev: 'Sign in with a development token',
    devHelp: 'Generate it with lastrouter dev-token. Works only on development installations.',
    token: 'Token',
    submit: 'Sign in',
    refused: 'Token refused. Generate another one and try again.',
    footer: 'Lastrouter: the context layer between AI agents and data sources.',
  },
  overview: {
    title: 'Overview',
    period: 'Last 7 days',
    calls: 'Calls',
    users: 'People',
    answered: 'Answered',
    clarifications: 'Asked for clarification',
    errors: 'Errors',
    negative: 'Negative feedback',
    license: 'License',
    publication: 'Published catalog',
    noPublication: 'No publication yet. Run lastrouter reload or publish a draft.',
    connections: 'Connections',
    pending: 'Pending',
    openDrafts: 'Drafts waiting for approval: {{count}}',
    openFeedback: 'Open feedback: {{count}}',
    nothingPending: 'Nothing pending.',
    publishedBy: 'Published by {{by}} on {{at}}',
    commit: 'Commit {{commit}}',
    noCommit: 'No Git commit',
  },
  license: { valida: 'Valid', tolerancia: 'Grace period', vencida: 'Expired', ausente: 'No license', invalida: 'Invalid', until: 'Until {{date}}' },
  connections: {
    title: 'Connections',
    subtitle: 'Data sources connected to Lastrouter. Only IT changes the ones in the configuration file.',
    id: 'Connection',
    type: 'Type',
    target: 'Address',
    state: 'State',
    checked: 'Checked at',
    managedBy: 'Managed by',
    arquivo: 'File',
    painel: 'Panel',
    ok: 'Connected',
    failed: 'Failing',
    test: 'Test as a user',
    testTitle: 'Test {{id}}',
    testHelp: 'Enter the user as the source knows them (e.g. CLUSTER\\maria). The test opens each app as that person, without reading values.',
    user: 'Source user',
    subject: 'Subject',
    allSubjects: 'All subjects',
    run: 'Test',
    summary: 'User {{user}} opens {{ok}} of {{total}} app(s).',
    app: 'App',
    appTitle: 'Title',
    lastReload: 'Last reload',
    empty: 'No source configured.',
  },
  context: {
    title: 'Context',
    subtitle: 'What each metric means, which source to query and which queries are validated.',
    tabPublished: 'Published',
    tabDrafts: 'Drafts',
    tabHistory: 'History',
    noCatalog: 'No catalog published yet.',
    export: 'Export zip',
    import: 'Import zip',
    newDraft: 'New draft',
    source: 'Source',
    app: 'App',
    owners: 'Owners',
    business: 'Business',
    technical: 'Technical',
    metrics: 'Metrics',
    dimensions: 'Dimensions',
    queries: 'Validated queries',
    playbooks: 'Analysis playbooks',
    unit: 'Unit',
    expression: 'Expression',
    synonyms: 'Synonyms',
    field: 'Field',
    type: 'Type',
    sensitive: 'Personal data',
    certified: 'Certified',
    notCertified: 'Not certified',
    version: 'v{{v}}',
    params: 'Parameters',
    required: 'required',
    optionalParam: 'optional',
    groupBy: 'Groups by',
    when: 'When to use',
    steps: 'Steps',
    dimTypes: { texto: 'Text', periodo_mensal: 'Month (YYYY-MM)', ano: 'Year', data: 'Date (derived period)' },
    history: {
      published: 'Published on',
      by: 'By',
      commit: 'Commit',
      reason: 'Reason',
      changes: 'Changes',
      active: 'Active',
      restore: 'Republish this version',
      restoreTitle: 'Republish {{id}}',
      restoreHelp: 'Brings the catalog back to this version. It becomes a new publication in the history.',
      restored: 'Version republished as {{id}}.',
      restoredFrom: 'back to {{id}}',
      dirty: 'with changes outside the commit',
    },
    drafts: {
      empty: 'No drafts. Create one from the published catalog.',
      reason: 'Reason',
      author: 'Author',
      created: 'Created on',
      state: 'State',
      subjects: 'Changed subjects',
      status: { aberto: 'Open', aprovado: 'Approved', publicado: 'Published', descartado: 'Discarded' },
      newTitle: 'New draft',
      newHelp: 'The draft starts equal to the published catalog. The reason shows in the history and to the owners who will approve.',
      create: 'Create draft',
      open: 'Open',
      importTitle: 'Import zip',
      importHelp: 'Use a zip of the context folder (same format as the repository). It becomes a draft; nothing is published without approval.',
      file: 'Zip file',
      imported: 'Draft created from the zip.',
    },
    editor: {
      title: 'Draft {{id}}',
      backToDrafts: 'Back to drafts',
      readOnly: 'Draft {{status}}: read only.',
      changes: 'What changes',
      noChanges: 'No changes compared to the published catalog.',
      approvals: 'Approvals',
      approvalsHelp: 'Each changed subject needs approval from its business owner and its technical owner.',
      pending: 'Pending: {{owner}}',
      approvedBy: 'Approved by {{by}}',
      refusedBy: 'Refused by {{by}}',
      approvedInPlace: 'Approved by {{by}} on behalf of {{owner}}',
      refusedInPlace: 'Refused by {{by}} on behalf of {{owner}}',
      substitute: 'Decide on behalf of the owner',
      substituteHelp: '{{owner}} cannot decide. As an admin, you decide on their behalf. The decision is recorded under your name and raises a security alert.',
      reason: 'Reason',
      approve: 'Approve',
      refuse: 'Refuse',
      comment: 'Comment',
      decisionTitle: '{{decision}} as {{papel}} owner of {{subject}}',
      papelNegocio: 'business',
      papelTecnico: 'technical',
      publish: 'Publish',
      publishHelp: 'Publishes once both owners of each changed subject approve.',
      published: 'Published as {{id}}.',
      discard: 'Discard',
      discardConfirm: 'Discard this draft? It can no longer be published.',
      discarded: 'Draft discarded.',
      validation: 'Validation',
      valid: 'Valid catalog.',
      invalid: 'Fix before saving:',
      validating: 'Checking',
      save: 'Save draft',
      saveHelp: 'Saving clears approvals already given.',
      savedDraft: 'Draft saved.',
      reasonLabel: 'Reason for the change',
      subject: 'Subject',
      ready: 'Ready to publish.',
      refusedState: 'A refusal blocks publishing until the draft changes.',
    },
    form: {
      id: 'Id',
      idHelp: 'Lowercase letters, numbers, _ and -.',
      name: 'Name',
      description: 'Description',
      synonymsHelp: 'Separate with commas.',
      unitHelp: 'Currency code (BRL), percentual or text (taxpayers).',
      expressionHelp: 'In the source language, with at least one aggregation. No $( ).',
      fieldHelp: 'Field name in the source.',
      sensitiveHelp: 'Kept out of value lists and the LLM, and masked in results.',
      example: 'Example question',
      exampleHelp: 'No personal data: this text goes to the LLM.',
      queryMetrics: 'Metrics (ids, comma separated)',
      queryGroupBy: 'Group by (dimension ids, comma separated)',
      param: 'Parameter',
      paramDimension: 'Dimension',
      paramRequired: 'Required',
      certifiedBy: 'Certified by',
      when: 'When to use',
      steps: 'Steps (one per line)',
      addMetric: 'Add metric',
      addDimension: 'Add dimension',
      addQuery: 'Add query',
      addParam: 'Add parameter',
      addPlaybook: 'Add playbook',
      editSubject: 'Edit subject',
      apply: 'Apply to draft',
      applied: 'Changes applied to the draft. Save to record them.',
      fixErrors: 'Fix the marked fields.',
    },
  },
  apps: {
    search: 'Search',
    searchPlaceholder: 'Name, Qlik id, space, subject or client',
    client: 'Client',
    anyClient: 'All clients',
    noClient: 'No client',
    source: 'Source',
    accessUnavailable: 'Could not check which apps you open in Qlik right now, so the list is empty. Try again in a few minutes.',
    emptyMine: 'You do not open any app in Qlik. Ask your Qlik administrator for access.',
    anySource: 'All sources',
    sort: 'Order',
    sorts: {
      name: 'Name (A to Z)',
      desc_name: 'Name (Z to A)',
      status: 'Status',
      client: 'Client',
      desc_last_reload: 'Most recent reload',
    },
    clear: 'Clear filters',
    statusFilter: 'Filter by status',
    all: 'All',
    statusFilters: {
      published: 'Published',
      draft: 'In draft',
      not_enabled: 'Not enabled',
      missing: 'Gone from the source',
    },
    name: 'App',
    status: 'Status',
    version: 'Version',
    liveVersion: 'v{{n}} live',
    draftVersion: 'draft v{{n}}',
    caption: 'Apps: {{count}}',
    select: 'Select {{name}}',
    selectPage: 'Select everything on this page',
    reloadedAt: 'Reload: {{when}}',
    noMatchFor: 'No app matches "{{q}}" with these filters.',
    noMatch: 'No app matches these filters.',
    empty: 'No apps registered yet. Use "Refresh Qlik app list" to bring in the apps you see in Qlik.',
    emptyOwner: 'You do not own any app yet.',
    range: '{{from}} to {{to}} of {{total}}',
    perPage: 'Per page',
    prev: 'Previous',
    next: 'Next',
    pageOf: 'Page {{page}} of {{pages}}',
    pagination: 'List pages',
    back: 'Back to the app list',
    unlinkOne: 'Remove {{name}} from the client',
    bulk: {
      label: 'Actions on the selected apps',
      selected: 'Selected: {{count}}',
      setClient: 'Link to a client',
      moveClient: 'Move to another client',
      unlink: 'Remove from client',
      enable: 'Enable',
      disable: 'Turn off',
      clearSelection: 'Clear selection',
      client: 'Client',
      noClient: 'No client (remove from client)',
      andMore: 'and {{count}} more',
      done: 'Done. Apps changed: {{count}}.',
      partial: 'Changed: {{changed}}. Failed on {{failed}}:',
      set_clientTitle: 'Link to a client',
      set_clientHelp: 'The apps now belong to the chosen client. Their context does not change.',
      set_clientConfirm: 'Link ({{count}})',
      set_clientNothing: 'No app selected.',
      unlinkTitle: 'Remove from client',
      unlinkHelp: 'The app stays registered, with its context intact; it just stops belonging to the client. The client\'s privacy mode no longer applies to it.',
      unlinkConfirm: 'Remove ({{count}})',
      unlinkNothing: 'None of the selected apps has a client.',
      unlinkSkipped: '{{count}} already have no client and stay as they are.',
      enableTitle: 'Enable apps',
      enableHelp: 'Each app gets an empty context draft to generate and review. Nothing reaches the channels before publishing.',
      enableConfirm: 'Enable ({{count}})',
      enableNothing: 'All selected apps are already enabled.',
      enableSkipped: '{{count}} are already enabled and stay as they are.',
      disableTitle: 'Turn off apps',
      disableHelp: 'The open draft is discarded and the published version is archived. The history is kept.',
      disableConfirm: 'Turn off ({{count}})',
      disableNothing: 'None of the selected apps is enabled.',
      disableSkipped: '{{count}} are already off and stay as they are.',
      disablePublished: '{{count}} published apps leave the channels now.',
    },
  },
  clients: {
    title: 'Clients',
    subtitle: 'The Qlik apps you open, with the context each one gives the chat and the other channels.',
    new: 'New client',
    create: 'Create client',
    created: 'Client created.',
    name: 'Name',
    context: 'Company context',
    contextHelp: 'What the agent needs to know about the company across all its apps: what it does, acronyms, audience and general rules. Markdown is allowed.',
    privacy: 'Privacy mode',
    privacyHelp: "The numbers of this client's apps never go to the AI: it knows what was queried, and the values show only to the person, in the table and chart below the answer. In Claude and other MCP clients, the values are withheld.",
    apps: 'Apps',
    appsCount: '{{published}} published of {{total}}',
    empty: 'No clients yet. Create the first one to link apps to it.',
    myApps: 'Apps',
    myAppsHelp: 'Apps whose context you can edit or approve.',
    noApps: 'No apps in this client.',
    back: 'Back to clients',
    export: 'Export',
    soon: 'Coming soon',
    delete: 'Delete client',
    deleteConfirm: 'Delete {{name}}? Its apps stay without a client, with their context intact.',
    deleted: 'Client deleted.',
    tabApps: 'Apps',
    tabContext: 'Company context',
    saved: 'Company context saved.',
    sync: 'Refresh Qlik app list',
    syncTitle: 'Refresh app list',
    syncHelp: 'Lists the source apps with your user and updates the registry. New apps come in without a client.',
    source: 'Source',
    noSources: 'No connections configured.',
    runSync: 'Refresh',
    syncResult: 'Listed as {{user}}: {{added}} new, {{updated}} updated, {{missing}} missing from the source.',
    addApp: 'Add apps',
    addAppTitle: 'Add app to {{name}}',
    addAppHelp: 'Search and check the apps without a client. If an app is missing, refresh the Qlik app list.',
    noUnassigned: 'There are no apps without a client. Refresh the Qlik app list.',
    titleBoth: 'Clients and apps',
    allApps: 'All apps',
    search: 'Search clients',
    noMatch: 'No client matches "{{q}}".',
    unassignedLink: 'Apps without a client: {{count}}',
    publishedShare: 'Published',
    appsAdded: 'Apps linked to the client: {{count}}.',
    addApps: 'Add ({{count}})',
    refineSearch: 'Showing the first 50 of {{count}}. Refine the search to find the app.',
  },
  assets: {
    batteryTabs: { run: 'Run', answerKey: 'Answer key', certification: 'Certification' },
    batteryRun: {
      trendShort: '+{{improved}} −{{regressed}} since the previous',
      criticalShort: 'critical {{score}}%',
      costLabel: 'Run cost',
      aboutSuggestions: 'About the improvements',
      aboutBattery: 'About the battery',
      aboutLearn: 'About Learn by itself',
      suggestionsShort: 'Improvements ({{count}} undecided)',
      why: 'Why',
      noRuns: 'No runs yet. Click Run.',
      runsCaption: 'Battery runs',
      runWhen: 'Run',
      runScore: 'Score',
      runQuestions: 'Questions',
      runTrend: 'Changed',
      cycleShort: 'cycle {{cycle}}/{{cycles}}',
      backToRuns: 'Runs',
      runOf: 'Run of {{when}}',
      tokens: 'LLM: {{calls}} calls, {{input}}k input tokens and {{output}}k output',
      validatedQuery: 'Validated query',
      alsoAsked: 'also asked as',
      verified: 'verified',
      revert: 'Undo',
      reverted: 'Improvement undone. Run the battery again to check.',
      learn: 'Learn by itself',
      learnHelp: 'Runs the battery, sends only verified improvements (formula equal to the answer key) to the draft and runs again, up to 3 cycles or 80%. Nothing is published by itself.',
      trend: '{{improved}} improved · {{regressed}} regressed since the previous run',
      cycle: 'cycle {{cycle}} of {{cycles}}',
      trends: { melhorou: 'improved', piorou: 'regressed', igual: 'same' },
      publishOne: 'Publish this one',
      refuseOne: 'Refuse',
      publishTitle: 'Publish this improvement now?',
      publishHelp: 'It enters the app context and applies in the chat right away. Since it skips the owners approval, it is recorded in the audit log with the reason.',
      draftHasOther: 'The app has an open draft with other changes: they will be published too.',
      publishNow: 'Publish now',
      publishing: 'Publishing...',
      published: 'Improvement published. Run the battery again to see the effect.',
      history: 'Run',
      historyOption: '{{when}} · score {{score}} · {{state}}',
      ok: 'OK (minimum {{min}}%)',
      belowMin: 'Below {{min}}%',
      newMeasure: 'New measure',
      synonyms: 'synonyms',
      suggestions: 'Suggested improvements ({{count}} undecided)',
      suggestionsHelp: 'Each wrong question generates a suggestion. The ones marked verified use the answer key formula and need no review; text ones do. Send them to the draft or publish one by one, then run the battery again: it tests the draft and shows what improved or regressed.',
      noSuggestions: 'No suggestions in this run.',
      pickSuggestion: 'Pick the suggestion for question {{id}}',
      pickAll: 'Select all undecided',
      toDraft: 'Send to draft ({{count}})',
      refuse: 'Refuse ({{count}})',
      applied: '{{count}} improvements sent to the draft. Run the battery again to see the effect.',
      draftReason: 'Improvements suggested by the battery',
      state: { aplicada: 'In the draft', publicada: 'Published', recusada: 'Refused', revertida: 'Undone' },
      archiveState: { concluida: 'finished', cancelada: 'cancelled', falhou: 'failed' },
      showQuestions: 'See the questions ({{count}})',
      noQuery: 'Built no query',
      noFormulaYet: 'Not asked yet',
      agentFormula: 'Agent formula',
      run: 'Run',
      search: 'Search',
      searchPlaceholder: 'Question, formula, answer or id',
      perPage: 'Per page',
      onlyOne: 'Showing only question {{id}}.',
      showAll: 'Show all',
      noMatch: 'No question matches this search.',
      noAnswerYet: 'No answer yet',
      noFormula: 'generated from the catalog',
      answered: '{{done}} of {{total}} questions asked',
      tally: '{{right}} right · {{wrong}} wrong',
      segmentsLabel: '{{right}} right and {{wrong}} wrong of {{total}} questions',
      title: 'Question battery',
      help: 'The agent gets each answer key question and builds the query. The battery compares the built formula with the answer key one. Nothing is computed in Qlik and no text answer is stored.',
      progress: 'Question {{done}} of {{total}} ({{pct}}%)',
      progressLabel: 'Battery progress',
      summary: 'Score {{score}}%: {{right}} right and {{wrong}} wrong. Run on {{when}}.',
      critical: 'Critical: {{score}}%.',
      notRun: '{{count}} questions ready. The battery has not run with them yet.',
      empty: 'No approved questions yet. Fill in the answer key above.',
      filter: 'Show',
      question: 'Question',
      expected: 'Expected formula',
      answer: 'Agent answer',
      result: 'Result',
      criticalTag: 'critical',
      pagination: 'Battery question pages',
      filters: { all: 'All ({{count}})', wrong: 'Wrong ({{count}})', right: 'Right ({{count}})', pending: 'Not scored yet ({{count}})' },
      results: { 'certo': 'Right', 'errado': 'Wrong', 'sem resposta': 'No answer', 'não avaliado': 'Not scored', 'respondido': 'Answered', 'aguardando': 'Waiting' },
    },
    answerKey: {
      download: 'Download JSON',
      howTitle: 'File format and prompt to generate it with AI',
      formatHelp: 'The file follows the {{format}} format: generation date (gerado_em, YYYY-MM-DD), app conventions and the list of tests with id, category, question, answer (the formula) and critical. The spreadsheet uses the same columns. The full format is in docs/gabarito.md.',
      privacyTitle: 'Agree with the client before using the prompt',
      privacy: 'The AI that builds the answer key reads the app in Qlik: field names, values and totals go to that AI provider. Use only an AI the client approved, with the data owner consent.',
      prompt: 'Prompt for an AI connected to Qlik',
      promptHelp: 'Paste it into an AI that can reach Qlik (for example, through MCP). It returns the JSON ready to import here.',
      copy: 'Copy prompt',
      copied: 'Prompt copied.',
      copyFailed: 'Could not copy. Select the text and copy it.',
      generatedAt: 'Answer key generated on {{date}}',
      conventions: 'App conventions',
      category: 'Category',
      categoryN: 'Category of question {{n}}',
      title: 'Answer key',
      help: 'Questions the agent must get right and the Qlik formula that answers each one. Qlik computes the expected value; it is never typed in. Each row becomes an approved battery case.',
      import: 'Import spreadsheet or JSON',
      paste: 'Paste JSON',
      add: 'Add question',
      empty: 'No questions yet. Import a spreadsheet with Question and Answer (the formula) columns, a JSON file, or add them by hand.',
      question: 'Question',
      formula: 'Answer (Qlik formula)',
      critical: 'Critical',
      questionN: 'Question {{n}}',
      formulaN: 'Formula for question {{n}}',
      criticalN: 'Question {{n}} is critical',
      removeN: 'Remove question {{n}}',
      pagination: 'Answer key pages',
      saved: 'Answer key saved with {{count}} questions. New formulas were computed in Qlik.',
      saving: 'Saving and computing in Qlik...',
      count: '{{count}} questions',
      incomplete: '{{count}} rows without a question or formula will not be saved',
      loaded: '{{count}} questions loaded. Check them and save.',
      warnings: '{{count}} rows ignored',
      pasteTitle: 'Paste the answer key as JSON',
      pasteHelp: 'A list of objects with question and answer (the formula). "formula" instead of "answer" and "critical": true also work.',
      pasteLoad: 'Load',
    },
    name: 'App',
    space: 'Space',
    status: 'Status',
    published: 'Published',
    draft: 'Draft',
    lastReload: 'Last reload',
    missing: 'Missing from source',
    missingHelp: 'The last list refresh did not find this app in the source: it was deleted or whoever refreshed lost access.',
    statuses: { nao_habilitado: 'Not enabled', rascunho: 'Draft', publicado: 'Published' },
    versionN: 'v{{n}}',
    qlikId: 'Qlik id',
    client: 'Client',
    noClient: 'No client',
    publishedVersion: 'Published version',
    publish: 'Publish',
    saveFirst: 'Save your changes before publishing.',
    publishedOk: 'Version {{n}} published.',
    editContext: 'Edit context',
    newDraftTitle: 'Open draft',
    newDraftHelp: 'The draft starts from the published version, or empty if nothing was published. The reason shows in the history.',
    newDraftFrom: 'The draft starts from version {{n}}. The reason shows in the history.',
    reason: 'Reason',
    create: 'Open draft',
    draftCreated: 'Draft v{{n}} opened.',
    draftExists: 'A draft is already open. Publish or discard it first.',
    disable: 'Turn off app',
    disableConfirm: 'Turn off {{name}}? The app leaves the catalog and the chat stops answering about it. The context is kept.',
    disabled: 'App turned off.',
    generate: {
      againTitle: 'Generate the context again',
      againHelp: 'Reads the app in Qlik again, takes a few minutes and creates a new proposal to review. The published context does not change until someone accepts the proposal, approves and publishes it.',
      notes: 'What you know about this app',
      notesHelp: 'Generation reads the app in Qlik with your access. If you want, first tell it what only someone who knows the app would know.',
      notesHint: 'Acronyms, business rules, what is left out, which numbers matter. The AI uses this to write what each item means; if a note does not match the formula, it says so and asks.',
      images: 'Images',
      imagesHint: 'Up to {{count}} screenshots or photos of documents. You can also paste a screenshot into the text field with Ctrl+V.',
      imagesMax: 'Up to {{count}} images.',
      imageUnreadable: 'Could not read that image. Use PNG, JPEG or WebP.',
      imageN: 'Image {{n}}',
      imageRemove: 'Remove image {{n}}',
      title: 'Generate context',
      help: 'Lastrouter reads the app model in Qlik as you (tables, fields, master measures and dimensions, sheets) and builds a context proposal with the risks it found. Nothing reaches the app until you apply the proposal to a draft.',
      start: 'Generate context',
      none: 'No generations yet.',
      runs: 'Generations',
      runsHint: 'The other tabs show the generation chosen here.',
      run: 'Generation',
      startedBy: 'Started by {{user}} on {{when}}',
      modelCounts: '{{tables}} tables, {{fields}} fields, {{measures}} master measures and {{dimensions}} master dimensions, {{sheets}} sheets',
      steps: {
        ler_modelo: 'Read the model in Qlik',
        extrair: 'Read the app in Qlik',
        planejar: 'Plan the checks',
        calcular: 'Calculate in the Qlik engine',
        interpretar: 'Interpret with AI',
        gerar: 'Build context and proposal',
        gravar: 'Save',
        bateria: 'Test battery against the agent'
      },
      cancel: 'Cancel',
      cancelled: 'Cancelled. What was already saved stays in the run.',
      cancelledTitle: 'Run cancelled',
      runningHint: 'This screen updates by itself. You can leave: the run keeps going on the server.',
      errorTitle: {
        ler_modelo: 'Could not read the app in Qlik',
        extrair: 'Could not read the app in Qlik',
        planejar: 'Could not plan the checks',
        calcular: 'The Qlik engine did not calculate the checks',
        interpretar: 'The AI could not interpret the app',
        gerar: 'Could not build the context',
        bateria: 'The test battery stopped halfway',
        other: 'The run stopped with an error'
      },
      errorHelp: 'Check that you can open this app in Qlik and try again. If it happens again, send the text below to support.',
      stepStatus: {
        rodando: 'Running',
        ok: 'Done',
        falhou: 'Failed'
      },
      runStatus: {
        rodando: 'running',
        ok: 'ready',
        falhou: 'failed',
        interrompida: 'interrupted'
      },
      findings: 'Risks found ({{n}})',
      findingsHelp: 'Critical and high risks become rules in the proposal Restrictions section.',
      noFindings: 'No risks found in the model.',
      severity: {
        critico: 'Critical',
        alto: 'High',
        medio: 'Medium',
        baixo: 'Low'
      },
      impact: 'Impact',
      recommendation: 'What to do',
      risk: {
        accept: 'Accept risk',
        accepted: 'Accepted',
        acceptedBy: 'Accepted by {{by}} on {{when}}: {{reason}}',
        reason: 'Reason',
        reasonHint: 'At least 10 characters. E.g.: selector table, unlinked on purpose.',
        confirm: 'Accept',
        reopen: 'Reopen',
        acceptedToast: 'Risk accepted. Certification updated.',
        reopenedToast: 'Risk reopened. Certification updated.'
      },
      applied: 'Proposal applied to the draft. Approvals were reset.',
      startAgain: 'Generate again',
      pipeline: {
        resultTitle: 'Result',
        resultHelp: 'What Lastrouter found in the app and what it proposes. Numbers come from the Qlik engine.',
        risks: 'Risks by severity',
        riskCount_one: '{{count}} risk',
        riskCount_other: '{{count}} risks',
        questions: 'Questions for the client',
        questionsHint: 'answered in the spreadsheet',
        ofTotal: '{{n}} of {{total}}',
        catalog: 'Recommended catalog',
        catalogHint: 'app items the agent should use, review or not see',
        expose: 'show',
        review: 'review',
        hide: 'hide',
        quality: 'Catalog quality',
        qualityHint: 'today → with the proposed context',
        numbers: 'Numbers status',
        numbersHint: 'totals checked in the engine',
        ai: 'AI used',
        aiDetail: '{{items}} items interpreted, {{removed_evidence}} unproven evidence removed, {{rewrites}} descriptions rewritten',
        noAi: 'No AI configured: the context has no written meaning for the items.',
        noData: 'no data',
        outOfProposal: 'Items left out of the proposal ({{n}})',
        outOfProposalHelp: 'They show in the app, but Lastrouter could not bring them into the context. Handle them by hand if needed.',
        downloadTitle: 'Download',
        downloadHelp: 'The spreadsheet goes to the client. The other files are the context in other formats.',
        files: {
          checkpoint_xlsx: 'Client spreadsheet',
          contexto_ossie_yaml: 'Context in Ossie format',
          vocabulario_qlik_answers_xlsx: 'Qlik Answers vocabulary',
          contexto_json: 'Context (JSON)',
          avaliacao_json: 'Download the evaluation',
          bateria_json: 'Battery (JSON)',
          snapshot_json: 'App reading (JSON)'
        },
        validationTitle: 'Client validation',
        validationHelp: 'Download the client spreadsheet; the client approves test cases, answers questions, decides the catalog and marks risks, and you upload the filled file here.',
        file: 'Filled spreadsheet (.xlsx, up to 10 MB)',
        send: 'Upload filled spreadsheet',
        sending: 'Uploading...',
        sent: 'Spreadsheet imported. Context, battery and certification were rebuilt.',
        tooBig: 'The file is over 10 MB. Upload only the filled spreadsheet.',
        changedTitle: 'What changed with the spreadsheet',
        changedCases: 'Approved cases: {{before}} → {{after}}',
        changedAnswers: 'Answered questions: {{before}} → {{after}} of {{total}}',
        batteryTitle: 'Battery and certification',
        batteryHelp: 'The battery asks the agent the questions the client approved, each in a new conversation, and compares the answers with the Qlik numbers. Certification says whether the context can go to production.',
        batteryCounts: '{{cases}} cases in the battery, {{critical}} critical, {{approved}} approved by the client.',
        runBattery: 'Run battery against the agent',
        noApproved: 'No approved case yet. The client approves cases in the Battery tab of the spreadsheet; upload it back and the button unlocks.',
        batteryRunning: 'Battery running. It takes a few minutes and this screen updates by itself.',
        released: 'Released for production',
        blocked: 'Certification incomplete (does not block publishing)',
        contextVersion: 'context {{v}}',
        score: 'Overall score',
        criticalScore: 'Critical cases score',
        noScore: 'Scores show up after the first battery against the agent.',
        conditionOk: 'met',
        conditionNo: 'not met'
      }
    },
    tabsLabel: 'App context steps',
    tabs: { summary: 'Summary', risks: 'Risks', context: 'Context', validation: 'Validation', battery: 'Test battery', publish: 'Publish', quality: 'Quality', versions: 'Versions' },
    tabState: { done: 'done', pending: 'pending', blocked: 'blocked' },
    nextStepTo: 'Next step: {{tab}}',
    nextHint: {
      summary: 'Generate the context: Lastrouter reads the app in Qlik and drafts a proposal.',
      risks: 'See the risks the generation found in the app model.',
      context: 'Compare the proposal with what is live today, section by section, and bring what makes sense into the draft.',
      validation: 'Download the spreadsheet, send it to the client and upload it back filled in.',
      battery: 'Run the test battery against the agent to get the release seal.',
      publish: 'Publish with the owners\' approval, or directly, with a recorded reason.',
      versions: 'See the published versions and what changed in each one.',
    },
    goTo: 'Open {{tab}}',
    needsRun: {
      none: 'This step uses the context generation, which has not run for this app yet.',
      unfinished: 'The latest generation did not finish. See its status in Summary.',
      old: 'This generation uses the old format, with no client spreadsheet or test battery. Generate again to get this step.',
    },
    review: {
      openAll: 'Expand all',
      closeAll: 'Collapse all',
      today: 'Live today',
      proposal: 'Generated proposal',
      draft: 'New text',
      whatChanges: 'What changes',
      diff: { add: 'added', del: 'removed' },
      emptyText: 'Empty.',
      tag: { suggested: 'New proposal', changed: 'Changed in the draft', same: 'No proposal' },
      accept: 'Accept the proposal',
      edit: 'Edit',
      keep: 'Keep what is live',
      runSuggests: 'See the text the generation proposes',
      fromRun: 'Proposal from the generation of {{when}}, by {{user}}',
      pending_one: '{{count}} part with a proposal to review.',
      pending_other: '{{count}} parts with a proposal to review.',
      nothingPending: 'Everything the generation proposed is already in the context.',
      acceptAll: 'Accept all proposals',
      saveFirst: 'Save the draft changes before accepting a proposal.',
      draftReason: 'Proposal from the context generation',
      semanticHelp: 'Metrics, dimensions, validated queries and playbooks the agent uses to query Qlik.',
      semanticCounts: '{{metrics}} metrics and {{dimensions}} dimensions',
      semanticFresh_one: 'The generation proposes {{count}} item the app does not have yet: {{names}}.',
      semanticFresh_other: 'The generation proposes {{count}} items the app does not have yet: {{names}}.',
      semanticNoFresh: 'The app already has every metric and dimension in the proposal.',
      addMissing: 'Add what is missing',
      replaceAll: 'Replace with the proposal',
      editSemantic: 'Edit metrics, dimensions and queries',
      seeSemantic: 'See metrics, dimensions and queries',
    },
    semantic: 'Semantic layer',
    versions: 'Versions',
    history: 'History',
    sections: {
      contexto: 'Context',
      regras: 'Business rules',
      premissas: 'Assumptions',
      restricoes: 'Restrictions and negatives',
      vocabulario: 'Vocabulary',
      sinonimos: 'Synonyms',
      exemplos: 'Examples and additional context',
      correcoes: 'Corrections',
      tom: 'Tone of voice',
    },
    sectionHelp: {
      contexto: 'What the app covers, where the data comes from and who it serves.',
      regras: 'How to calculate and read the numbers in this app.',
      premissas: 'What the agent may assume when the question does not say, such as the default period or unit.',
      restricoes: 'What the agent must not do or answer. Mandatory.',
      vocabulario: 'Business acronyms and terms, with their meaning.',
      sinonimos: 'Other names people use for metrics, dimensions and values.',
      exemplos: 'Example questions and context that helps answer.',
      correcoes: 'Corrections made by people. They override the rules.',
      tom: 'How the assistant talks about this app: formal or friendly, words to avoid, how to address people. Overrides the general tone in Appearance.',
    },
    noDraft: 'You are viewing the published version. To change it, click Edit on a section, type and save. The change is kept aside and only reaches the chat after it is approved and published.',
    nothing: 'This app is not enabled yet. Enable it in the apps list to generate and edit its context.',
    saveReason: 'Reason for the change',
    saveHelp: 'Saving stores the whole draft and resets any approvals already given.',
    saved: 'Draft saved.',
    stale: 'Someone else saved this draft before you. Reload to see the current version; your local changes will be lost.',
    reload: 'Reload',
    issues: 'What blocks publishing',
    approvals: 'Approvals for draft v{{n}}',
    approvalsHelp: 'The draft needs approval from the business owner and the technical owner.',
    discard: 'Discard draft',
    discardConfirm: 'Discard draft v{{n}}? It can no longer be published.',
    discarded: 'Draft discarded.',
    noVersions: 'No versions yet.',
    openFrom: 'Open a draft from this version',
    versionsTable: { number: 'Version', state: 'State', by: 'By', when: 'When', reason: 'Reason and changes' },
    states: { rascunho: 'Draft', publicada: 'Published', arquivada: 'Archived', descartada: 'Discarded' },
    changes: {
      when: 'When',
      who: 'Who',
      part: 'Part',
      operation: 'Operation',
      reason: 'Reason',
      before: 'Before',
      after: 'After',
      show: 'Show text',
      empty: 'No changes recorded.',
    },
    operations: { add: 'Added', replace: 'Changed', remove: 'Removed' },
    changeClient: 'Change client',
    setClient: 'Link to a client',
    changeClientHelp: 'The app moves to the chosen client; its context does not change.',
    clientRemoved: 'App removed from the client.',
    clientChanged: 'App client changed.',
    unlinkConfirm: 'Remove {{app}} from {{client}}?',
    unvalidated: {
      noticeBy: 'By {{by}} on {{when}}.',
      reason: 'Reason',
      reasonHint: 'At least 10 characters. The reason is kept on the version, in the alert and in the audit log.',
      pendingTitle_one: '{{count}} pending item',
      pendingTitle_other: '{{count}} pending items',
      dialogTitle: 'Publish version {{n}} directly',
      dialogHelp: 'The context goes to the agents without the full flow. Missing approvals are recorded in your name, on behalf of the owners, and the version is marked.',
      confirm: 'Publish directly',
      open: 'Publish directly...',
      done: 'Version {{n}} published directly.',
      title: 'Publish directly',
      help: 'Admin shortcut for when the client has not validated yet or the owners cannot approve. It asks for a reason, raises an alert and goes to the audit log.',
      normalTitle: 'Publish with approval',
      normalHelp: 'The normal path: both owners approve the draft and the admin publishes.',
      ownerPending: {
        negocio: 'Business owner approval missing ({{owner}}).',
        tecnico: 'Technical owner approval missing ({{owner}}).',
      },
      adminOnly: 'Only the administrator publishes.',
      nothingPending: 'Nothing pending. Use Publish.',
      refused: '{{who}} refused this version. Edit the draft or ask for a new decision: the shortcut does not override a refusal.',
      nothingToPublish: 'Version {{n}} is published and no change is waiting. To change the context, edit it in the Context tab and save.',
    },
  },
  users: {
    title: 'Users',
    subtitle: 'Who uses Lastrouter and with which role. Roles live in Entra ID and this page changes Entra directly, without the Azure portal. The apps each person sees come from Qlik.',
    addButton: 'Give someone access',
    giveAccess: 'Give access',
    search: 'Search',
    searchPlaceholder: 'Name or email',
    roleFilter: 'Role',
    noRole: 'No role',
    caption: '{{count}} people',
    person: 'Person',
    role: 'Role in Entra',
    qlikUser: 'Qlik user',
    lastSeen: 'Last access',
    never: 'Never used',
    you: 'you',
    group: 'Group',
    origin: { entra: 'Assigned in Entra', audit: 'Audit only' },
    roles: { reader: 'Reader', editor: 'Editor', admin: 'Admin' },
    roleHelp: { reader: 'makes dynamic queries and analyzes', editor: 'also proposes context fixes in chat', admin: 'also uses the panel' },
    roleOf: 'Role of {{name}}',
    changed: '{{name}} is now {{role}}.',
    selfLock: 'You cannot remove your own Admin role. Ask another administrator.',
    empty: 'Nobody with a role in Entra or in the last 90 days of audit.',
    noMatch: 'Nobody with that name or role.',
    graph: {
      not_configured: 'The panel is not connected to Entra',
      forbidden: 'Lastrouter is not yet allowed to change roles in Entra',
      error: 'Entra did not respond',
      not_connected: 'Connect your account to see and change Entra roles',
      connect: 'Connect to Entra',
      ok: '',
      steps: 'Until then, the list shows who appears in the audit, and adding, changing or removing roles is off.',
    },
    add: {
      title: 'Give someone access',
      help: 'Find the person in the Entra directory and pick the role. The change applies at their next sign-in.',
      role: 'Role',
      search: 'Name or email',
      searchPlaceholder: 'maria or maria@company.com',
      searchButton: 'Search',
      noResults: 'Nobody found in the directory.',
      give: 'Give access as {{role}}',
      done: '{{name}} now has the {{role}} role.',
    },
    remove: {
      button: 'Remove access',
      aria: 'Remove access for {{name}}',
      title: 'Remove access for {{name}}?',
      help: 'The person loses the Lastrouter roles in Entra and stops using the chat, Teams and the panel at the next sign-in. You can give access again later.',
      confirm: 'Remove access',
      done: '{{name}} no longer has access.',
    },
  },
  access: {
    title: 'Access',
    subtitle: 'IdP roles say what a person can do: Reader makes dynamic queries, Editor also corrects context from the chat, Admin uses the panel. The apps they see come from Qlik.',
    subject: 'Subject',
    source: 'Source',
    owners: 'Owners',
    simTitle: 'Simulate a person',
    simHelp: 'Shows the tools these roles allow and the apps the person sees in Qlik. Lists the apps as them, without querying data.',
    upn: 'Login (UPN)',
    roles: 'Roles (one per line)',
    clientApp: 'Client app (client id)',
    simulate: 'Simulate',
    channel: 'Channel',
    refused: 'Refused: {{reason}}',
    tools: 'Tools',
    subjects: 'Subjects',
    sourceUser: 'Source user',
  },
  people: {
    title: 'People',
    subtitle: 'Who uses Lastrouter, with which role and which limit.',
    tabUsers: 'Users',
    tabLimits: 'Limits per person',
    tabSimulate: 'Simulate access',
  },
  products: {
    status: 'Status',
    configured: 'Configured',
    notConfigured: 'Not configured',
    fromFile: 'The configuration file (lastrouter.yaml) applies today. When you save a change here, the panel takes over for every channel, starting from a copy of the file.',
    fromPanel: 'Channel settings are saved in the panel and override the file.',
    backToFile: 'Use the file again',
    backToFileHelp: 'The settings saved in the panel for every channel stop applying, and the configuration file applies again. Per-channel limits stay.',
    savedFile: 'The configuration file applies again.',
    saved: 'Channel settings saved.',
    channelHelp: 'What this channel can do with the context. Changes apply immediately, without a restart.',
    staging: 'Staging',
    tools: 'What the channel can do',
    toolsHelp: 'Fewer options make answers more accurate.',
    toolsRequired: 'Choose at least one.',
    limit: 'Queries per minute, per person',
    limitHelp: 'Empty: {{n}} per minute applies.',
    limitHelpFile: 'Empty: the general limit in Settings applies.',
    privateValues: 'Shows the values on the person\'s screen',
    privateValuesHelp: 'For clients in privacy mode. On: the channel shows the numbers only to the person who asked, and the AI never sees them. Off: the channel gets the answer without the values. Turn it on only for Chat and Ask in Teams, which can hide numbers from the AI.',
    advanced: 'Advanced',
    channelId: 'Channel id',
    channelIdHelp: 'Short lowercase name shown in Audit and Conversations.',
    idError: 'Use lowercase letters, numbers, - or _, and do not repeat another channel.',
    clientIds: 'Client app ids in Entra (one per line)',
    clientIdsHelp: 'The application (client) id the agent signs in with. Empty: only Lastrouter itself uses this channel.',
    removeConnector: 'Remove connector',
    removeConnectorHelp: '{{name}} stops using Lastrouter. Other channels do not change.',
    noChannel: 'This channel has no settings yet (id "{{id}}"). Without them it cannot use Lastrouter.',
    createChannel: 'Set up',
    copy: 'Copy',
    copied: 'Copied.',
    teams: {
      title: 'Ask in Teams',
      subtitle: 'Ask, the data assistant that uses Lastrouter\'s context, inside Microsoft Teams.',
      channel: 'Settings in Lastrouter',
      lastQuestion: 'Last dynamic query from Teams',
      noQuestion: 'None in recent days',
      package: 'App package',
      packageHelp: 'The version is in the Teams app package (zip), built by the Cluster team. This page does not read the version yet.',
      installTitle: 'How to install in Teams',
      install1: 'Ask the Cluster team for the Teams app package (.zip file).',
      install2: 'In the Teams admin center, under Teams apps > Manage apps, upload the package or update the existing app.',
      install3: 'Make the app available to the people who will use it and check it with a test dynamic query.',
      channelTitle: 'Ask in Teams settings',
    },
    chat: {
      title: 'Chat',
      subtitle: 'Lastrouter\'s chat in the browser, to test the context or for everyday use.',
      open: 'Open the chat',
      address: 'Address',
      login: 'Sign-in with Entra',
      help: 'People with a Lastrouter role sign in with the organization account and see the apps they see in Qlik.',
      off: 'The chat is not on yet: the chat section is missing from the configuration file. Talk to IT.',
      channelTitle: 'Chat settings',
    },
    mcp: {
      title: 'MCP connectors',
      subtitle: 'Claude, Copilot and other agents using Lastrouter\'s context through the MCP protocol.',
      add: 'Add connector',
      connectTitle: 'How to connect',
      address: 'MCP address',
      claude1: 'In Claude, open Settings > Connectors and choose to add a custom connector.',
      claude2: 'Paste the MCP address above.',
      claude3: 'Sign in with the organization account when Claude asks.',
      copilot1: 'In Copilot Studio, open the agent and go to Tools > Add a tool > Model Context Protocol.',
      copilot2: 'Paste the MCP address and choose OAuth sign-in with Entra.',
      copilot3: 'Add a connector here with the Copilot application id, under Advanced.',
      newConnector: 'New connector',
      empty: 'No connectors. Use "Add connector" to allow Claude, Copilot or another agent.',
      kinds: {
        claude: 'Claude ({{id}})',
        copilot: 'Copilot ({{id}})',
        other: 'Other connector ({{id}})',
      },
    },
  },
  limits: {
    subtitle: 'Queries per minute each person can make. Each channel and each person can have their own limit; it applies immediately, without a restart.',
    global: 'General limit per person',
    globalHelp: 'Empty: the configuration file value applies.',
    perUser: 'Exceptions per person',
    perUserHelp: 'For people who need a limit different from the default. Applies immediately.',
    limit: 'Queries per minute',
    user: 'Login',
    addUser: 'Add exception',
    noUsers: 'No exceptions.',
    saved: 'Limits saved.',
    invalid: 'Use a whole number from 1 to 10,000.',
  },
  quality: {
    title: 'Quality',
    subtitle: 'How the agent is answering, per subject and per week.',
    tabWeekly: 'Answer rate',
    tabFeedback: 'Feedback',
    tabProposals: 'Proposals',
    tabCandidates: 'Questions for the suite',
    tabReports: 'Suite results',
    chartTitle: 'Answer rate per week',
    chartHelp: 'Share of dynamic queries answered without asking for clarification. Weeks with the marker dropped more than 10 points and need review with the subject owner.',
    showTable: 'Show table',
    showChart: 'Show chart',
    week: 'Week',
    subject: 'Subject',
    questions: 'Dynamic queries',
    answered: 'Answered',
    clarifications: 'Clarifications',
    noAnswer: 'No answer',
    errors: 'Errors',
    negative: 'Negative feedback',
    rate: 'Answer rate',
    review: 'Review',
    noSubject: 'No subject',
    noData: 'No dynamic queries recorded yet.',
    feedback: {
      when: 'When',
      kind: 'Type',
      comment: 'Comment',
      query: 'Query',
      state: 'State',
      routed: 'Routed to',
      kinds: { wrong: 'Wrong answer', review: 'Review request', useful: 'Useful' },
      states: { aberto: 'Open', em_revisao: 'In review', resolvido: 'Resolved', descartado: 'Discarded' },
      change: 'Change state',
      resolution: 'Resolution',
      empty: 'No feedback with this filter.',
      saved: 'State updated.',
    },
    proposals: {
      generate: 'Generate proposals',
      generated: 'Proposals generated: {{count}}.',
      kinds: { sinonimo: 'Synonym', exemplo: 'Example', nova_query: 'New query', revisao: 'Review' },
      states: { proposta: 'Proposed', aceita: 'Accepted', recusada: 'Refused' },
      accept: 'Accept',
      refuse: 'Refuse',
      evidence: 'Evidence',
      acceptHelp: 'Accepting does not change the catalog: open a draft with the change.',
      empty: 'No proposals. Generate them from problem conversations.',
    },
    candidates: {
      question: 'Question',
      reason: 'Why',
      empty: 'No candidate questions.',
      help: 'Real questions without an answer or with negative feedback. Take the relevant ones to perguntas.yaml with the right answer per user.',
    },
    reports: {
      label: 'Run',
      when: 'When',
      answerRate: 'Answer',
      accuracy: 'Accuracy',
      certifiedWrong: 'Certified wrong',
      deterministic: 'Deterministic',
      open: 'Open report',
      empty: 'No suite results yet. Run lastrouter eval.',
    },
  },
  channels: {
    toolNames: {
      search_context: 'Search the context',
      list_values: 'List the values of a field',
      run_validated_query: 'Run a validated query',
      explain_result: 'Explain a result',
      resolve_question: 'Understand the query',
      report_feedback: 'Record the person\'s feedback',
      query_metric: 'Query a metric',
      list_apps: 'List the apps',
      describe_app: 'Describe an app',
      query: 'Query data',
      propose_context_change: 'Propose a context fix',
    },
  },
  conversations: {
    find: 'Search conversations',
    findPlaceholder: 'Query, answer or person',
    noMatch: 'No loaded conversation matches "{{q}}".',
    title: 'Conversations',
    subtitle: 'The dynamic queries people made, how they were answered and their feedback. Result values are not kept here.',
    defaultPeriod: 'With no date, shows the last 7 days.',
    question: 'Dynamic query',
    answer: 'Answer',
    feedback: 'Feedback',
    useful: 'Useful',
    wrong: 'Wrong',
    noFeedback: 'No feedback',
    ok: 'Answered',
    detail: 'Conversation',
    noQuestion: 'The call did not include the query.',
    auditOnly: 'This channel has no bot of its own: Lastrouter sees the tool calls, not the answer text.',
    auditShort: 'Answer text stays in the client',
    model: 'Model',
    rounds: 'LLM rounds',
    tokens: 'LLM tokens',
    tokensValue: '{{input}} input ({{cached}} cached), {{output}} output, in {{calls}} calls',
    unverified: 'Numbers without a source',
    steps: 'Steps',
    noSteps: 'No tool was called.',
    results: 'Results shown',
    resultsGone: '{{count}} result(s) past retention or from an app you cannot see.',
    filters: 'Filters',
    noFilters: 'none',
    except: 'except',
    range: 'from {{from}} to {{to}}',
    certified: 'Certified',
    notCertified: 'Not certified',
    executedAt: 'Computed at',
    caveats: 'Caveats',
    replay: 'Replay the query',
    empty: 'No conversations match these filters.',
    shown: '{{count}} conversation(s)',
    truncated: 'Showing the {{count}} most recent. Shorten the period to see the rest.',
  },
  audit: {
    title: 'Audit',
    subtitle: 'One line per tool call, with the executed expression. No result values.',
    from: 'From',
    to: 'To',
    user: 'User',
    tool: 'Tool',
    status: 'State',
    subject: 'Subject',
    any: 'All',
    filter: 'Filter',
    clear: 'Clear',
    when: 'When',
    channel: 'Channel',
    query: 'Query',
    rows: 'Rows',
    duration: 'Duration',
    statuses: { ok: 'Answered', clarification: 'Clarification', no_answer: 'No answer', error: 'Error' },
    beforeTool: 'Refused before the tool',
    loadMore: 'Load more',
    export: 'Export CSV',
    verify: 'Check hash chain',
    chainOk: 'Chain intact: {{records}} lines in {{files}} file(s).',
    chainBroken: 'Chain broken in {{file}}, line {{line}}: {{reason}}',
    detail: 'Audit line',
    empty: 'No lines with these filters.',
    shown: '{{count}} line(s) on screen',
    expressions: 'Executed expressions',
    publication: 'Catalog publication',
    promptModel: 'Prompt and model',
    versions: 'Versions',
    lastReload: 'App last reload',
    tokens: 'Tokens (input / output)',
    result: 'Result',
    hash: 'Chain hash',
  },
  appearance: {
    title: 'Appearance',
    subtitle: 'Only the brand layer changes: assistant name, accent color, logo and icon. Structure and typography belong to Cluster.',
    name: 'Assistant name',
    voice: 'Tone of voice',
    voiceHelp: 'How the assistant talks in every channel and app. An app with a Tone of voice section in its context uses its own.',
    voicePlaceholder: 'E.g.: formal and direct, no slang, explain acronyms the first time.',
    color: 'Accent color',
    colorHelp: 'Needs 4.5:1 with white text and 3:1 on the dark background.',
    onWhite: 'White text on the color: {{ratio}}:1',
    onDark: 'Color on the dark background: {{ratio}}:1',
    passes: 'Contrast approved.',
    fails: 'This color fails the contrast check. Pick a darker one.',
    invalidColor: 'Use the #RRGGBB format.',
    logo: 'Logo',
    icon: 'Icon',
    upload: 'Upload image',
    removeImage: 'Remove',
    imageHelp: 'PNG, JPEG or WebP up to 200 KB.',
    tooLarge: 'The image is larger than 200 KB.',
    wrongType: 'Use PNG, JPEG or WebP.',
    preview: 'Answer preview',
    previewQuestion: 'How much did Porto Alegre collect in September last year?',
    previewAnswer: 'R$1,234,567.89 in September 2025, Porto Alegre',
    certified: 'Certified',
    useful: 'Useful',
    wrong: 'Wrong',
    askReview: 'Ask for review',
    saved: 'Appearance saved.',
  },
  settings: {
    title: 'Settings',
    subtitle: 'General usage limit and system information. The configuration file comes from lastrouter.yaml and only IT changes it.',
    version: 'Version',
    edition: 'Edition',
    instance: 'Instance',
    tenant: 'Tenant',
    backend: 'State stored in',
    uptime: 'Up for',
    license: 'License',
    customer: 'Customer',
    validity: 'Valid until',
    features: 'Features',
    lastCheckin: 'Last check-in',
    config: 'Configuration file (no secrets)',
    editions: { selfhosted: 'Self-hosted', cloud: 'Cloud' },
    backends: { files: 'Files', postgres: 'PostgreSQL' },
  },
};
